// Client for penalties and allowance entitlements.
//
// Both are scoped server-side: someone without the policy panel at Manage sees
// only their OWN penalties, and can only preview their own allowances. The
// `scope` field on the penalties response says which happened, so the UI can
// label the page honestly rather than pretending it shows everything.

import api from './api';

export type PenaltyStatus = 'PENDING' | 'APPROVED' | 'WAIVED' | 'REJECTED';

export interface Penalty {
  id: string;
  emptID: string;
  rule_key: string | null;
  reason_code: string | null;
  description: string | null;
  amount: number;
  year: number;
  month: number;
  status: PenaltyStatus;
  levied_by: string | null;
  levied_at: string | null;
  decision_by: string | null;
  decision_at: string | null;
  decision_note: string | null;
  ref_type: string | null;
  ref_id: string | null;
  recovered_in_salary_id: string | null;
}

export interface PenaltyList {
  penalties: Penalty[];
  scope: 'branch' | 'self';
  total_approved: number;
}

export interface LevyPayload {
  employee_id: string;
  rule_key: string;
  year: number;
  month: number;
  reason_code?: string;
  description?: string;
  missed_days?: number;
  amount_override?: number;
  ref_type?: string;
  ref_id?: string;
}

export async function listPenalties(params?: {
  employeeId?: string;
  year?: number;
  month?: number;
  status?: PenaltyStatus;
}): Promise<PenaltyList> {
  const { data } = await api.get('/policy-money/penalties', {
    params: {
      ...(params?.employeeId ? { employee_id: params.employeeId } : {}),
      ...(params?.year ? { year: params.year } : {}),
      ...(params?.month ? { month: params.month } : {}),
      ...(params?.status ? { status: params.status } : {}),
    },
  });
  return data;
}

export async function levyPenalty(body: LevyPayload): Promise<Penalty> {
  const { data } = await api.post('/policy-money/penalties', body);
  return data;
}

export async function decidePenalty(
  penaltyId: string,
  status: 'APPROVED' | 'WAIVED' | 'REJECTED',
  note?: string,
): Promise<Penalty> {
  const { data } = await api.put(`/policy-money/penalties/${penaltyId}`, { status, note });
  return data;
}

export interface Entitlement {
  date: string;
  rule_key: string;
  label: string;
  amount: number;
  reason: string;
}

export interface AllowancePreview {
  year: number;
  month: number;
  employee_id: string | null;
  // False for admin / head-office accounts, which have no employee row at all.
  // They get an empty, explained page rather than an error.
  has_profile?: boolean;
  entitlements: Entitlement[];
  total: number;
  declared_commute?: number | null;
  missing_declaration?: boolean;
  // Present when there is nothing to derive from — no payroll breakdown for
  // the month, or no employee profile on the account.
  note?: string;
}

export async function previewAllowances(
  year: number,
  month: number,
  employeeId?: string,
): Promise<AllowancePreview> {
  const { data } = await api.get('/policy-money/allowances/preview', {
    params: { year, month, ...(employeeId ? { employee_id: employeeId } : {}) },
  });
  return data;
}

export interface ConveyanceAdjustment {
  line_index: number;
  category: string;
  claimed_amount: number;
  usual_commute_amount: number;
  reimbursable_amount: number;
  offset: number;
  financial_year: string;
}

export interface ConveyancePreview {
  // False for accounts with no employee row; the claim then stands at its full
  // amount, which is what the submit path does too.
  has_profile?: boolean;
  differential_applies: boolean;
  declared_commute: number | null;
  missing_declaration: boolean;
  adjustments: ConveyanceAdjustment[];
  total_claimed: number;
  total_reimbursable: number;
  checks: { ok: boolean; issues: Array<{ code: string; message: string }> };
}

// What a conveyance claim will actually reimburse, before it is filed — the
// same computation the submit path runs, so the figure can't change afterwards.
export async function previewConveyance(body: {
  spend_date: string;
  lines: Array<{ category: string; amount: number }>;
  mode?: string;
  left_at?: string;
  submitted_on?: string;
}): Promise<ConveyancePreview> {
  const { data } = await api.post('/policy-money/conveyance/preview', body);
  return data;
}

export async function saveCommuteDeclaration(body: {
  financial_year: string;
  usual_commute_amount: number;
  notes?: string;
}, employeeId?: string): Promise<{
  id: string; financial_year: string; usual_commute_amount: number;
}> {
  const { data } = await api.put('/policy-money/allowance-declaration', body, {
    params: employeeId ? { employee_id: employeeId } : undefined,
  });
  return data;
}
